import { CKEditorCloudResult } from '@ckeditor/ckeditor5-angular';
import {
  ImageResize,
  ImageStyle,
  type ClassicEditor,
  type Editor,
  type EditorConfig,
} from 'https://cdn.ckeditor.com/typings/ckeditor5.d.ts';
import { createEmailImageWrapperGuard } from './ckeditor-email-image-wrapper-guard';
import { createAlignedDivTableGuard } from './ckeditor-aligned-div-table-guard';
import { createContentStylePlugin } from './ckeditor-content-style-plugin';
import { createUnsafeHtmlGuard, HTML_SUPPORT } from './ckeditor-unsafe-html-guard';

const IMAGE_RESIZE_HELPER_TEXT = 'Enter a width in pixels. Height adjusts automatically.';

const IMAGE_BOX_DECLARATION =
  /^(margin|padding)(-(top|right|bottom|left))?$|^border(-(top|right|bottom|left))?(-(width|style|color))?$|^float$/;

// the margin, padding, border and float the author set on an image or figure. The export's stylesMap also holds
// ckeditor5.css's declarations, so the element's own style attribute (untouched at that point) is re-read instead
function ownImageBoxDeclarations(el: HTMLElement): Array<[string, string]> {
  const declarations: Array<[string, string]> = [];
  for (const declaration of (el.getAttribute('style') ?? '').split(';')) {
    const colon = declaration.indexOf(':');
    if (colon < 0) continue;
    const name = declaration.slice(0, colon).trim().toLowerCase();
    const value = declaration.slice(colon + 1).trim();
    if (value && IMAGE_BOX_DECLARATION.test(name)) declarations.push([name, value]);
  }
  return declarations;
}

// shows helper text under the width input of the image "Resize image" popup; CKEditor has no config option for it
function ImageResizeHelperText(editor: Editor): void {
  const balloon = editor.plugins.get('ContextualBalloon');
  balloon.on('change:visibleView', () => {
    // _form is private to ImageCustomResizeUI; compare instances so a minified class name can't break the match
    const form = (editor.plugins.get('ImageCustomResizeUI') as unknown as { _form?: unknown })._form;
    const view = balloon.visibleView as { labeledInput?: { infoText: string | null } } | null;
    if (view && view === form && view.labeledInput && view.labeledInput.infoText !== IMAGE_RESIZE_HELPER_TEXT) {
      view.labeledInput.infoText = IMAGE_RESIZE_HELPER_TEXT;
      // the balloon was positioned before the text was added, so reposition it
      editor.ui.update();
    }
  });
}

export function buildCKEditorConfig(
  cloud: CKEditorCloudResult<{ version: '48.1.1'; premium: true }>,
  licenseKey: string,
  tokenUrl: () => Promise<string>
): {
  Editor: typeof ClassicEditor;
  config: EditorConfig;
} {
  const {
    ClassicEditor,
    Autosave,
    Essentials,
    Paragraph,
    PasteFromOffice,
    FindAndReplace,
    Fullscreen,
    SpecialCharactersArrows,
    SpecialCharacters,
    SpecialCharactersCurrency,
    SpecialCharactersEssentials,
    SpecialCharactersLatin,
    SpecialCharactersMathematical,
    SpecialCharactersText,
    Autoformat,
    TextTransformation,
    Mention,
    SimpleUploadAdapter,
    ImageInline,
    ImageUpload,
    ImageInsert,
    ImageInsertViaUrl,
    AutoImage,
    ImageBlock,
    ImageToolbar,
    CloudServices,
    MediaEmbed,
    Bold,
    Italic,
    Underline,
    Strikethrough,
    Code,
    Superscript,
    Subscript,
    FontBackgroundColor,
    FontColor,
    FontFamily,
    FontSize,
    RemoveFormat,
    Highlight,
    Heading,
    Link,
    AutoLink,
    BlockQuote,
    HorizontalLine,
    Indent,
    IndentBlock,
    Alignment,
    Style,
    GeneralHtmlSupport,
    HtmlComment,
    List,
    ListProperties,
    Table,
    TableToolbar,
    TableLayout,
    PlainTableOutput,
    TableProperties,
    TableCellProperties,
    TableColumnResize,
    TableCaption,
    ShowBlocks,
    HtmlEmbed,
    Emoji,
    ImageCaption,
    ImageResize,
    ImageStyle,
    LinkImage,
  } = cloud.CKEditor;

  const {
    FormatPainter,
    CaseChange,
    PasteFromOfficeEnhanced,
    ExportWord,
    ExportPdf,
    ImportWord,
    ExportInlineStyles,
    LineHeight,
    MultiLevelList,
    TableOfContents,
    SourceEditingEnhanced,
    EmailConfigurationHelper,
  } = cloud.CKEditorPremiumFeatures;

  const config: EditorConfig = {
    licenseKey,
    cloudServices: { tokenUrl },
    plugins: [
      createAlignedDivTableGuard(cloud.CKEditor), // ATLAS-5792: keep before upcast; see ckeditor-aligned-div-table-guard.ts
      createContentStylePlugin(cloud.CKEditor), // ATLAS-5793: content <style> blocks (scoped display, head-hoist fix) and htmlSpan order; see ckeditor-content-style-plugin.ts
      createUnsafeHtmlGuard(cloud.CKEditor), // ATLAS-5831: removes the htmlSupport disallow list where GHS cannot; see ckeditor-unsafe-html-guard.ts
      createEmailImageWrapperGuard(cloud.CKEditor), // ATLAS-5882: unwraps the email image table before upcast; see ckeditor-email-image-wrapper-guard.ts
      Alignment,
      Autoformat,
      AutoImage,
      AutoLink,
      Autosave,
      BlockQuote,
      Bold,
      CaseChange,
      CloudServices,
      Code,
      EmailConfigurationHelper,
      Essentials,
      ExportInlineStyles,
      ExportPdf,
      ExportWord,
      FindAndReplace,
      FontBackgroundColor,
      FontColor,
      FontFamily,
      FontSize,
      FormatPainter,
      Fullscreen,
      GeneralHtmlSupport,
      Heading,
      Highlight,
      HorizontalLine,
      HtmlComment,
      HtmlEmbed,
      ImageBlock,
      ImageInsertViaUrl,
      ImageToolbar,
      ImageCaption,
      ImageInline,
      ImageResize,
      ImageResizeHelperText,
      ImageStyle,
      ImportWord,
      Indent,
      IndentBlock,
      Italic,
      LineHeight,
      Link,
      LinkImage,
      List,
      ListProperties,
      MediaEmbed,
      Mention,
      MultiLevelList,
      Paragraph,
      PasteFromOffice,
      PasteFromOfficeEnhanced,
      PlainTableOutput,
      RemoveFormat,
      ShowBlocks,
      SimpleUploadAdapter,
      SourceEditingEnhanced,
      SpecialCharacters,
      SpecialCharactersArrows,
      SpecialCharactersCurrency,
      SpecialCharactersEssentials,
      SpecialCharactersLatin,
      SpecialCharactersMathematical,
      SpecialCharactersText,
      Strikethrough,
      Style,
      Subscript,
      Superscript,
      Table,
      TableCaption,
      TableCellProperties,
      TableColumnResize,
      TableLayout,
      TableOfContents,
      TableProperties,
      TableToolbar,
      TextTransformation,
      Underline,
      Emoji,
    ],
    toolbar: {
      items: [
        // ═══ ROW 1 — Format the text ═══════════════════════════
        'undo',
        'redo',
        '|',
        'heading',
        'style',
        '|',
        'fontSize',
        'fontFamily',
        'fontColor',
        'fontBackgroundColor',
        'highlight',
        '|',
        'bold',
        'italic',
        'underline',
        'strikethrough',
        'subscript',
        'superscript',
        'code',
        '|',
        'formatPainter',
        'caseChange',
        'removeFormat',
        '-',
        // ═══ ROW 2 — Put things in, then finish ════════════════
        'alignment',
        'lineHeight',
        'bulletedList',
        'numberedList',
        'multiLevelList',
        'outdent',
        'indent',
        '|',
        'link',
        'insertImage',
        'insertTable',
        'insertTableLayout',
        'horizontalLine',
        'blockQuote',
        'specialCharacters',
        'emoji',
        'tableOfContents',
        'htmlEmbed',
        '|',
        'showBlocks',
        'findAndReplace',
        'fullscreen',
        '|',
        'sourceEditingEnhanced',
        'importWord',
        'exportWord',
        'exportPdf',
      ],
      shouldNotGroupWhenFull: true,
    },
    exportInlineStyles: {
      stylesheets: [
        'https://cdn.ckeditor.com/ckeditor5/48.1.1/ckeditor5.css',
        'https://cdn.ckeditor.com/ckeditor5-premium-features/48.1.1/ckeditor5-premium-features.css',
      ],
      transformations: [
        (element, stylesMap) => {
          if (element.tagName === 'IMG') {
            const el = element as HTMLElement;
            const inlineWidth = el.style.width;
            const inlineHeight = el.style.height;
            stylesMap.clear();
            ownImageBoxDeclarations(el).forEach(([name, value]) => stylesMap.set(name, value));
            if (inlineWidth) stylesMap.set('width', inlineWidth);
            if (inlineHeight) stylesMap.set('height', inlineHeight);
          }
          if (element.tagName === 'FIGURE') {
            const el = element as HTMLElement;
            const inlineWidth = el.style.width;
            const inlineHeight = el.style.height;
            const floatRight = el.classList.contains('image-style-side') || el.classList.contains('image-style-align-right');
            const floatLeft = el.classList.contains('image-style-align-left');
            stylesMap.clear();
            ownImageBoxDeclarations(el).forEach(([name, value]) => stylesMap.set(name, value));
            if (inlineWidth) stylesMap.set('width', inlineWidth);
            if (inlineHeight) stylesMap.set('height', inlineHeight);
            if (floatRight) stylesMap.set('float', 'right');
            else if (floatLeft) stylesMap.set('float', 'left');
          }
          // ATLAS-5884: the stylesheet's `.table` margin makes every exported table look centred, so mark the ones the author
          // centred inline; the email cleanup turns the marker into Outlook's align="center"
          if (element.tagName === 'TABLE') {
            const el = element as HTMLElement;
            if (el.style.marginLeft === 'auto' && el.style.marginRight === 'auto' && !el.style.float) {
              el.setAttribute('data-export-align', 'center');
            }
          }
          // keep <hr> bare, as CKEditor 4 saved it, instead of inlining the editor's .ck-content hr rule
          if (element.tagName === 'HR') {
            stylesMap.clear();
          }
        },
      ],
    },
    exportPdf: {
      stylesheets: [
        'https://cdn.ckeditor.com/ckeditor5/48.1.1/ckeditor5.css',
        'https://cdn.ckeditor.com/ckeditor5-premium-features/48.1.1/ckeditor5-premium-features.css',
      ],
      fileName: 'document.pdf',
      converterOptions: {
        document: {
          size: 'A4',
          orientation: 'portrait',
          margins: {
            top: '0mm',
            bottom: '0mm',
            right: '0mm',
            left: '0mm',
          },
        },
      },
    },
    exportWord: {
      stylesheets: [
        'https://cdn.ckeditor.com/ckeditor5/48.1.1/ckeditor5.css',
        'https://cdn.ckeditor.com/ckeditor5-premium-features/48.1.1/ckeditor5-premium-features.css',
      ],
      fileName: 'document.docx',
      converterOptions: {
        document: {
          size: 'A4',
          orientation: 'portrait',
          margins: {
            top: '1in',
            bottom: '1in',
            right: '1in',
            left: '1in',
          },
          language: 'en',
        },
      },
    },
    fontSize: {
      options: [10, 12, 14, 'default', 18, 20, 22],
      supportAllValues: true,
    },
    fullscreen: {
      onEnterCallback: (container) =>
        container.classList.add(
          'editor-container',
          'editor-container_classic-editor',
          'editor-container_include-outline',
          'editor-container_include-style',
          'editor-container_include-fullscreen',
          'main-container'
        ),
    },
    // map Heading 1-6 to h1-h6 as CKEditor 4 did; the default maps Heading 1 to h2 and has no h1 (ATLAS-5760)
    heading: {
      options: [
        { model: 'paragraph', title: 'Paragraph', class: 'ck-heading_paragraph' },
        { model: 'heading1', view: 'h1', title: 'Heading 1', class: 'ck-heading_heading1' },
        { model: 'heading2', view: 'h2', title: 'Heading 2', class: 'ck-heading_heading2' },
        { model: 'heading3', view: 'h3', title: 'Heading 3', class: 'ck-heading_heading3' },
        { model: 'heading4', view: 'h4', title: 'Heading 4', class: 'ck-heading_heading4' },
        { model: 'heading5', view: 'h5', title: 'Heading 5', class: 'ck-heading_heading5' },
        { model: 'heading6', view: 'h6', title: 'Heading 6', class: 'ck-heading_heading6' },
      ],
    },
    // ATLAS-5831: everything CKEditor 4 kept, except the disallow list in ckeditor-unsafe-html-guard.ts
    // ATLAS-5883: the data-wl-inlined record relies on the allow-all attribute rule; narrowing it would freeze inlined values, as they would read as the author's
    htmlSupport: HTML_SUPPORT,
    image: {
      resizeUnit: 'px',
      toolbar: [
        'resizeImage:custom',
        '|',
        'imageStyle:inline',
        'imageStyle:alignLeft',
        'imageStyle:alignRight',
        'imageStyle:block',
        'imageStyle:side',
        '|',
        'toggleImageCaption',
        'imageTextAlternative',
        '|',
        'linkImage',
      ],
    },
    lineHeight: {
      supportAllValues: true,
    },
    link: {
      addTargetToExternalLinks: true,
      defaultProtocol: 'https://',
      decorators: {
        toggleDownloadable: {
          mode: 'manual',
          label: 'Downloadable',
          attributes: {
            download: 'file',
          },
        },
      },
    },
    list: {
      properties: {
        styles: true,
        startIndex: true,
        reversed: false,
      },
    },
    mention: {
      feeds: [
        {
          marker: '@',
          feed: [],
        },
      ],
    },
    style: {
      definitions: [
        {
          name: 'Article category',
          element: 'h3',
          classes: ['category'],
        },
        {
          name: 'Title',
          element: 'h2',
          classes: ['document-title'],
        },
        {
          name: 'Subtitle',
          element: 'h3',
          classes: ['document-subtitle'],
        },
        {
          name: 'Info box',
          element: 'p',
          classes: ['info-box'],
        },
        {
          name: 'CTA Link Primary',
          element: 'a',
          classes: ['button', 'button--green'],
        },
        {
          name: 'CTA Link Secondary',
          element: 'a',
          classes: ['button', 'button--black'],
        },
        {
          name: 'Marker',
          element: 'span',
          classes: ['marker'],
        },
        {
          name: 'Spoiler',
          element: 'span',
          classes: ['spoiler'],
        },
      ],
    },
    table: {
      contentToolbar: ['tableColumn', 'tableRow', 'mergeTableCells', 'tableProperties', 'tableCellProperties'],
      // ATLAS-5884: write alignment as inline float/margin, not a table-style-align-* class whose CSS exists only in .ck-content.
      // Class-form alignment in stored content still upcasts. The version test in ckeditor-config.spec.ts fails on any
      // ckeditor5 bump: confirm both facts in @ckeditor/ckeditor5-table, then update the version.
      tableProperties: { alignment: { useInlineStyles: true } },
    },
  };

  return { Editor: ClassicEditor, config };
}
